import {
  faChevronCircleLeft,
  faChevronCircleRight,
  faChevronLeft,
  faChevronRight,
} from "@fortawesome/free-solid-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { FunctionComponent, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { Document, Page, pdfjs } from "react-pdf";

pdfjs.GlobalWorkerOptions.workerSrc = new URL(
  "pdfjs-dist/build/pdf.worker.min.mjs",
  import.meta.url,
).toString();

interface PromiseWithResolvers<T> {
  promise: Promise<T>;
  resolve: (value: T | PromiseLike<T>) => void;
  reject: (reason?: any) => void;
}

// This is a polyfill for pdfjs-dist
if (typeof Promise.withResolvers === "undefined") {
  if (typeof window !== "undefined") {
    (window.Promise as any).withResolvers = function <
      T,
    >(): PromiseWithResolvers<T> {
      let resolve!: (value: T | PromiseLike<T>) => void;
      let reject!: (reason?: any) => void;

      const promise = new Promise<T>((res, rej) => {
        resolve = res;
        reject = rej;
      });

      return { promise, resolve, reject };
    };
  }
}

interface PdfViewerProps {
  url: string;
  setLoading: (loading: boolean) => void;
  className?: string;
  onPageChange: (page: number) => void;
  getTotalPageNumber: (page: number) => void;
}

const PdfViewer: FunctionComponent<PdfViewerProps> = ({
  url,
  setLoading,
  className = "",
  onPageChange,
  getTotalPageNumber,
}) => {
  const { t } = useTranslation();
  const [numPages, setNumPages] = useState<number>(1);
  const [pageNumber, setPageNumber] = useState<number>(1);

  const preloadPages = useMemo(() => {
    const firstPageNumber = 1;
    const lastPageNumber = numPages;

    const pages = [];

    if (pageNumber > firstPageNumber) {
      pages.push(firstPageNumber);
    }
    if (pageNumber > firstPageNumber + 1) {
      pages.push(pageNumber - 1);
    }
    pages.push(pageNumber);
    if (pageNumber < lastPageNumber - 1) {
      pages.push(pageNumber + 1);
    }
    if (pageNumber < lastPageNumber) {
      pages.push(lastPageNumber);
    }

    return pages;
  }, [numPages, pageNumber]);

  function onDocumentLoadSuccess({ numPages }: { numPages: number }) {
    setNumPages(numPages);
    getTotalPageNumber(numPages);
    setPage(1);
    setTimeout(() => setLoading(false), 10);
  }

  function setPage(page: number) {
    setPageNumber(page);
    onPageChange(page);
  }

  const handleFirst = () => {
    setPage(1);
  };

  const handlePrevious = () => {
    setPage(pageNumber - 1);
  };

  const handleNext = () => {
    setPage(pageNumber + 1);
  };

  const handleLast = () => {
    setPage(numPages);
  };

  const renderPagination = () => {
    const firstButton = (
      <span
        className={`previous ${pageNumber <= 1 ? "disabled" : ""}`}
        onClick={handleFirst}
      >
        <a>
          <FontAwesomeIcon icon={faChevronCircleLeft} />
          <span className="text">{t("common.first")}</span>
        </a>
      </span>
    );
    const previousButton = (
      <span
        className={`previous ${pageNumber <= 1 ? "disabled" : ""}`}
        onClick={handlePrevious}
      >
        <a>
          <FontAwesomeIcon icon={faChevronLeft} />
          <span className="text">{t("common.previous")}</span>
        </a>
      </span>
    );
    const nextButton = (
      <span
        className={`previous ${pageNumber >= numPages ? "disabled" : ""}`}
        onClick={handleNext}
      >
        <a>
          <span className="text">{t("common.next")}</span>
          <FontAwesomeIcon icon={faChevronRight} />
        </a>
      </span>
    );
    const lastButton = (
      <span
        className={`previous ${pageNumber >= numPages ? "disabled" : ""}`}
        onClick={handleLast}
      >
        <a>
          <span className="text">{t("common.last")}</span>
          <FontAwesomeIcon icon={faChevronCircleRight} />
        </a>
      </span>
    );
    return (
      <div className="pagination">
        {numPages > 1 && firstButton}
        {numPages > 1 && previousButton}
        {pageNumber + "/" + numPages}
        {numPages > 1 && nextButton}
        {numPages > 1 && lastButton}
      </div>
    );
  };

  return (
    <div className={`app-pdf-viewer ${className}`}>
      {renderPagination()}
      <div className="pdf">
        <Document
          file={url}
          loading="Chargement..."
          onLoadSuccess={onDocumentLoadSuccess}
        >
          {preloadPages.map((preloadedPageNumber) => (
            <Page
              key={`${url}_${preloadedPageNumber}`}
              className={
                preloadedPageNumber === pageNumber ? "active" : "d-none"
              }
              pageNumber={preloadedPageNumber}
              renderTextLayer={false}
              renderAnnotationLayer={false}
            />
          ))}
        </Document>
      </div>
      {renderPagination()}
    </div>
  );
};

export default PdfViewer;
